import { Seo } from '@/components/Seo';
import { JsonLd, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { Link } from 'react-router-dom';
import { GraduationCap, BookOpen, Award, Users, FileText } from 'lucide-react';

const faqs = [
  {
    question: 'Is ai-free-resume good for students with no experience?',
    answer: 'Yes. The Student template and tips help you highlight coursework, projects, volunteer work, and skills instead of full-time jobs.',
  },
  {
    question: 'What should I put on my resume if I have never had a job?',
    answer: 'Include education, relevant coursework, projects, volunteer work, extracurriculars, certifications, and a clear objective or summary.',
  },
  {
    question: 'Can I use this for internships?',
    answer: 'Yes. The student resume builder is designed for internships and entry-level jobs.',
  },
  {
    question: 'Is it free for students?',
    answer: 'Yes. Building and downloading your resume is free. You also get 2 free AI rewrites per browser ID.',
  },
  {
    question: 'Will my resume look professional?',
    answer: 'Yes. Our Student template is clean and ATS-friendly, so your resume looks polished even with limited experience.',
  },
];

export function ResumeBuilderForStudents() {
  return (
    <>
      <Seo
        title="Resume Builder for Students | Free, No Sign-Up"
        description="Build a free resume for students with no work experience. ATS-friendly templates, AI bullet rewriter, and instant PDF download."
        path="/resume-builder-for-students"
      />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'Resume Builder for Students', path: '/resume-builder-for-students' }])} />

      <section className="hero-glow py-24 md:py-32">
        <div className="mx-auto max-w-[800px] px-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00D4AA]/15">
            <GraduationCap size={32} className="text-[#00D4AA]" />
          </div>
          <h1 className="text-balance mt-6 text-4xl font-extrabold text-white md:text-5xl">
            Resume Builder for Students — Land Your First Job or Internship
          </h1>
          <p className="mx-auto mt-6 max-w-[680px] text-lg text-white/75">
            ai-free-resume.org offers a free resume builder for students with no work experience. It helps you highlight coursework, skills, and projects in a professional PDF.
          </p>
          <Link
            to="/app/create"
            className="btn-primary mt-8 inline-flex items-center justify-center rounded-[10px] px-8 py-4 text-base font-semibold"
          >
            Build My Student Resume
          </Link>
        </div>
      </section>

      <section className="bg-[#0A2540] py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#00D4AA]/15 px-4 py-1.5 text-sm font-semibold text-[#00D4AA]">
              Stand out
            </span>
            <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl">
              Stand out, even with little experience
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: BookOpen, title: 'Coursework', desc: 'List relevant classes that match the job or internship.' },
              { icon: Award, title: 'Projects', desc: 'Showcase class, personal, or hackathon projects with outcomes.' },
              { icon: Users, title: 'Volunteer Work', desc: 'Frame volunteer roles as real work experience.' },
              { icon: FileText, title: 'Skills', desc: 'Include technical, language, and soft skills that employers want.' },
            ].map((s) => (
              <div key={s.title} className="glass-card rounded-2xl p-6 transition duration-300 hover:-translate-y-1 hover:border-[rgba(0,212,170,0.35)]">
                <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-[#00D4AA]/15">
                  <s.icon size={24} className="text-[#00D4AA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-[15px] text-white/75">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              Student resume tips
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540] md:text-4xl">
              How to write a student resume that gets noticed
            </h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {[
              { title: 'Lead with education', desc: 'Since you have less work experience, put your education near the top. Include your major, expected graduation, and GPA if it is strong.' },
              { title: 'Use action verbs', desc: 'Start bullet points with words like led, built, organized, designed, or analyzed.' },
              { title: 'Quantify when possible', desc: 'Numbers make your experience more credible. Managed 15 volunteers, raised $500, or completed 40-hour project.' },
              { title: 'Tailor to the role', desc: 'Read the job description and mirror the language and skills it asks for.' },
            ].map((t) => (
              <div key={t.title} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 transition duration-300 hover:bg-white hover:shadow-sm">
                <h3 className="text-xl font-semibold text-[#0A2540]">{t.title}</h3>
                <p className="mt-2 text-[#4A5568]">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F6F9FC] py-16">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">
              Student Resume FAQ
            </h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Build your student resume today"
        subtitle="Free, private, and ATS-friendly. No sign-up required."
        buttonText="Build My Student Resume"
        buttonTo="/app/create"
      />
    </>
  );
}

export default ResumeBuilderForStudents;
